import { useLayoutEffect, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";

import { Box, Theme, Typography } from "@mui/material";
import { Tooltip } from "@shared/Tooltip";

export const fieldSlotMinHeight = 60;
const fieldInputHeight = 40;

const useStyles = makeStyles()((theme: Theme) => ({
    field: {
        display: "grid",
        // Capped in px rather than a percentage of the panel: a percentage keeps growing with the
        // panel, and in a full-width panel it strands the value hundreds of pixels away from the
        // label it belongs to.
        gridTemplateColumns: "minmax(140px, 260px) minmax(0, 1fr)",
        columnGap: theme.spacing(3),
        alignItems: "baseline",
        minWidth: 0,
        paddingTop: theme.spacing(0.25),
        paddingBottom: theme.spacing(0.25),
    },
    // Label and value differ by size and color only. Do not reintroduce a weight step here: the
    // pages built out of SectionField would then highlight every single value on the page.
    fieldLabel: {
        color: theme.palette.text.secondary,
        minWidth: 0,
    },
    fieldValue: {
        color: theme.palette.text.primary,
        minWidth: 0,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    fieldRow: {
        display: "grid",
        gridTemplateColumns: "minmax(140px, 260px) minmax(0, 1fr)",
        columnGap: theme.spacing(3),
        alignItems: "start",
        minWidth: 0,
    },
    fieldRowLabel: {
        display: "flex",
        alignItems: "center",
        minHeight: fieldInputHeight,
    },
    fields: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(0.5),
        minWidth: 0,
    },
    formStack: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1.5),
        minWidth: 0,
    },
    formGrid: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: theme.spacing(1.5, 5),
        minWidth: 0,
        [theme.breakpoints.down("md")]: {
            gridTemplateColumns: "1fr",
            gap: theme.spacing(1.5),
        },
    },
    inputSlot: {
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        minHeight: fieldSlotMinHeight,
        paddingTop: theme.spacing(1),
    },
    fullWidth: {
        gridColumn: "1 / -1",
    },
}));

type SectionFieldProps = {
    label: string;
    value: React.ReactNode;
};

function useIsClipped(dependency: unknown) {
    const ref = useRef<HTMLDivElement>(null);
    const [clipped, setClipped] = useState(false);

    useLayoutEffect(() => {
        const check = () => {
            if (ref.current) {
                setClipped(ref.current.scrollWidth > ref.current.clientWidth);
            }
        };

        check();
        window.addEventListener("resize", check);
        return () => window.removeEventListener("resize", check);
    }, [dependency]);

    return { ref, clipped };
}

export function SectionField(props: SectionFieldProps) {
    const { classes } = useStyles();
    const tooltipTitle = typeof props.value === "string" ? props.value : "";
    const { ref, clipped } = useIsClipped(props.value);

    return (
        <Box className={classes.field}>
            <Typography variant="body1" className={classes.fieldLabel}>
                {props.label}
            </Typography>
            <Tooltip title={tooltipTitle} hide={!clipped || !tooltipTitle} disableChildrenWrapper>
                <Typography variant="body1" ref={ref} className={classes.fieldValue} component="div">
                    {props.value}
                </Typography>
            </Tooltip>
        </Box>
    );
}

type SectionFieldsProps = {
    children: React.ReactNode;
};

export function SectionFields(props: SectionFieldsProps) {
    const { classes } = useStyles();
    return <Box className={classes.fields}>{props.children}</Box>;
}

type SectionFieldRowProps = {
    label: string;
    children: React.ReactNode;
};

export function SectionFieldRow(props: SectionFieldRowProps) {
    const { classes, cx } = useStyles();
    return (
        <Box className={classes.fieldRow}>
            <Typography variant="body1" className={cx(classes.fieldLabel, classes.fieldRowLabel)}>
                {props.label}
            </Typography>
            {props.children}
        </Box>
    );
}

type SectionFormStackProps = {
    children: React.ReactNode;
};

export function SectionFormStack(props: SectionFormStackProps) {
    const { classes } = useStyles();
    return <Box className={classes.formStack}>{props.children}</Box>;
}

type SectionFormGridProps = {
    children: React.ReactNode;
};

export function SectionFormGrid(props: SectionFormGridProps) {
    const { classes } = useStyles();
    return <Box className={classes.formGrid}>{props.children}</Box>;
}

type SectionInputSlotProps = {
    children: React.ReactNode;
    fullWidth?: boolean;
};

export function SectionInputSlot(props: SectionInputSlotProps) {
    const { classes, cx } = useStyles();
    return <Box className={cx(classes.inputSlot, props.fullWidth && classes.fullWidth)}>{props.children}</Box>;
}
